// 261005 E1-P1 — job viết kịch bản do PHIÊN NÀY bắt đầu. Dời nguyên logic từ `AdaptationTab.tsx:73-190`
// (scriptJob / progress / notice / startScriptJob / writeRequestGeneration). Giữ ở vỏ: SeasonReaderCard,
// khung Trợ lý AI (thẻ nối tập) và nút phục hồi của hộp lỗi đều gọi `writeScripts` từ ngoài chặng ⑤.
import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { adaptationApi } from '../../../services/api/adaptation';
import type { FilmWriteScriptsTaskDetail } from '../../../types/adaptation';
import {
  batchStopExit, describeAdaptationError, describeBatchStop, describeScriptProgress, isCurrentWriteScriptsRequest,
  projectFilmWriteScriptsTask, AdaptationErrorView,
} from '../adaptationUtils';
import { useFilmJobPoll } from '../useFilmJobPoll';
import { friendlyJobNote } from './jobErrorText';
import { isImpactChangedError } from './consequenceView';

/** `exit:'openOverview'` ⇒ dải thông báo có nút "Mở Tổng quan truyện" (lô dừng vì tổng quan — F1 review FE). */
export type ScriptNotice = { type: 'success' | 'warning' | 'error'; message: string; exit?: 'openOverview' };
/** `expectedFingerprint` (3h4c): vân tay của «hộp hậu quả» viết lại một tập; lệch ⇒ BE 409 `IMPACT_CHANGED`, chưa mở job. */
export interface ScriptWriteOpts { from?: number; to?: number; force?: boolean; expectedFingerprint?: string }

export interface ScriptJobApi {
  scriptJob: { id: string; label: string } | null;
  progress: string | null;
  notice: ScriptNotice | null;
  /** Đang gọi API khởi động. */
  starting: boolean;
  /** Cửa viết kịch bản dùng chung (lô / từng tập / phục hồi tuần tự). */
  /** Trả `'IMPACT_CHANGED'` (không báo lỗi) khi gửi kèm `expectedFingerprint` mà BE báo dữ liệu đã đổi — người gọi (hộp hậu quả) tự tải lại hệ quả. */
  writeScripts: (opts?: ScriptWriteOpts, label?: string) => Promise<void | 'IMPACT_CHANGED'>;
}

export const useScriptJob = (
  storyId: string,
  onFinished: () => void,
  showError: (v: AdaptationErrorView) => void,
  /** Job viết kịch bản BE báo đang chạy (`pickScriptCandidate(workbench.jobs.running)`) — nhận nuôi sau tải lại / đổi truyện (F36). */
  candidate: string | null = null,
): ScriptJobApi => {
  const currentStoryIdRef = useRef(storyId);
  const generation = useRef(0);
  currentStoryIdRef.current = storyId;
  const [scriptJob, setScriptJobState] = useState<{ id: string; label: string } | null>(null);
  const doneIds = useRef<Set<string>>(new Set());
  const setScriptJob = useCallback((v: { id: string; label: string } | null) => {
    setScriptJobState((cur) => { if (cur && (!v || v.id !== cur.id)) doneIds.current.add(cur.id); return v; });
  }, []);
  const [notice, setNotice] = useState<ScriptNotice | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    generation.current++;
    setScriptJobState(null); doneIds.current = new Set(); setNotice(null); setProgress(null); setStarting(false);
  }, [storyId]);

  useEffect(() => {
    if (!scriptJob && !starting && candidate && !doneIds.current.has(candidate)) setScriptJobState({ id: candidate, label: 'Viết kịch bản' });
  }, [scriptJob, starting, candidate]);

  const writeScripts = useCallback(async (opts: ScriptWriteOpts = {}, label?: string): Promise<void | 'IMPACT_CHANGED'> => {
    if (scriptJob) return;
    const text = label ?? (opts.from !== undefined
      ? (opts.from === opts.to ? `Viết lại tập ${opts.from}` : `Viết lại tuần tự từ tập ${opts.from}`)
      : 'Viết lô kịch bản');
    const requestGeneration = ++generation.current;
    const requestStoryId = storyId;
    const isCurrent = () => isCurrentWriteScriptsRequest(requestGeneration, generation.current, requestStoryId, currentStoryIdRef.current);
    setStarting(true);
    setNotice(null);
    try {
      const response = await adaptationApi.writeScripts(storyId, opts);
      if (!isCurrent()) return;
      if (!response.jobId) {
        setNotice({ type: 'warning', message: 'Server không trả job id; đã dừng theo dõi và không tự gửi lại.' });
        return;
      }
      setScriptJob({ id: response.jobId, label: text });
      message.info(`${text} đã bắt đầu ở nền`, 5);
    } catch (e: any) {
      if (!isCurrent()) return;
      if (opts.expectedFingerprint && isImpactChangedError(e)) return 'IMPACT_CHANGED';   // hộp hậu quả báo câu không mã + tải lại hệ quả
      // Y4: lỗi range/nối tập có nút phục hồi ⇒ hộp lỗi thay vì toast.
      showError(describeAdaptationError(e));
    } finally {
      if (isCurrent()) setStarting(false);
    }
  }, [scriptJob, storyId, showError, setScriptJob]);

  useFilmJobPoll<FilmWriteScriptsTaskDetail>(scriptJob?.id ?? null, {
    onTick: (task) => {
      const p = projectFilmWriteScriptsTask(task);
      const stop = describeBatchStop(p);
      setProgress(stop ? `${describeScriptProgress(p)} · ${stop}` : describeScriptProgress(p));
    },
    onTerminal: async (outcome, task) => {
      const jobId = scriptJob?.id ?? '';
      let n: ScriptNotice;
      if (outcome === 'completed') {
        const projection = projectFilmWriteScriptsTask(task);
        const stop = describeBatchStop(projection);
        n = {
          type: projection.failed || stop ? 'warning' : 'success',
          message: `Viết xong ${projection.written}/${projection.total} tập · hỏng ${projection.failed} · bỏ qua ${projection.skipped}.${stop ? ` ${stop}.` : ''}`,
          ...(batchStopExit(projection) ? { exit: batchStopExit(projection)! } : {}),
        };
      } else if (outcome === 'failed') {
        const projection = projectFilmWriteScriptsTask(task);
        n = { type: 'error', message: friendlyJobNote(projection.message) || 'Job viết kịch bản đã thất bại. Các tập đã ghi trước lỗi vẫn được giữ.' };
      } else if (outcome === 'unsupported') {
        n = { type: 'warning', message: `Job ${jobId.slice(0, 8)} có trạng thái không thuộc Film. Đã dừng theo dõi và không tự chạy lại.` };
      } else if (outcome === 'timeout') {
        n = { type: 'warning', message: `Đã dừng theo dõi job ${jobId.slice(0, 8)} sau 30 phút. Job có thể vẫn chạy; hãy bấm Tải lại, không chạy lại.` };
      } else {
        n = { type: 'warning', message: `Mất kết nối khi theo dõi job ${jobId.slice(0, 8)}. Job có thể vẫn chạy; hãy bấm Tải lại, không chạy lại.` };
      }
      onFinished();
      setScriptJob(null);
      setProgress(null);
      setNotice(n);
      if (n.type === 'success') message.success(n.message, 8);
      else if (n.type === 'error') message.error(n.message, 10);
      else message.warning(n.message, 10);
    },
  });

  return { scriptJob, progress, notice, starting, writeScripts };
};
